import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  EmailVerificationPurpose,
  UserSystemStatus,
  VerifyEmailTokenDocument,
} from '../queries';
import { emailAtom } from '../state/atoms';
import { getStatusRoute } from '../utils/status-route';
import { toVerifiedStatus } from '../utils/verified-status';
import { useSetUserStatus } from './use-set-user-status';

export type VerifiedEmailToken =
  | { status: 'pending' }
  | { status: 'invalid' }
  | { status: 'verified'; email: string };

/**
 * Verifies the `token` search param for the given purpose. A verified email
 * whose status is not one of `allowedStatuses` is sent to its status route;
 * otherwise the status is stored in the registration atoms.
 * `allowedStatuses` must be a stable reference.
 */
export const useVerifiedEmailToken = (
  purpose: EmailVerificationPurpose,
  allowedStatuses: ReadonlyArray<UserSystemStatus>
): VerifiedEmailToken => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = useAtomValue(emailAtom);
  const setUserStatus = useSetUserStatus();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(VerifyEmailTokenDocument, {
    variables: { token: token ?? '', purpose },
    skip: !token,
    fetchPolicy: 'no-cache',
  });
  const result = data?.verifyEmailToken;

  useEffect(() => {
    if (!token) {
      navigate('/register', { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    const verified = toVerifiedStatus(result);
    if (!verified) {
      return;
    }
    if (allowedStatuses.includes(verified.userStatus)) {
      setUserStatus(verified);
      return;
    }
    const route = getStatusRoute(verified.userStatus);
    if (route.type === 'redirect') {
      window.location.assign(route.url);
    } else {
      navigate(route.path, { replace: true });
    }
  }, [result, allowedStatuses, navigate, setUserStatus]);

  const verified = toVerifiedStatus(result);
  if (!token || loading) {
    return { status: 'pending' };
  }
  if (error || !verified) {
    return { status: 'invalid' };
  }
  // Wait for a redirect, or for the atoms to hold the verified status
  if (
    !allowedStatuses.includes(verified.userStatus) ||
    email !== verified.email
  ) {
    return { status: 'pending' };
  }
  return { status: 'verified', email: verified.email };
};
